import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { usersService, operadorasService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  MARCAS,
  MARCAS_PERMITIDAS_DEFAULT,
  resolvePapMarcas,
  resolveMarcasPermitidas,
  toggleMarcaInArray
} from '../utils/marcas';
import '../styles/Usuarios.css';

const Usuarios = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [todosUsuarios, setTodosUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [editingUsuario, setEditingUsuario] = useState(null);
  const [showNovoUsuario, setShowNovoUsuario] = useState(false);
  const [operadoras, setOperadoras] = useState([]);
  const [usuarioOperadoras, setUsuarioOperadoras] = useState([]);
  const [atenderTodas, setAtenderTodas] = useState(false);
  const [novoUsuarioRole, setNovoUsuarioRole] = useState('');
  const [editingTelefones, setEditingTelefones] = useState([]);
  const [novosTelefones, setNovosTelefones] = useState([]);
  const [novoPapMarcas, setNovoPapMarcas] = useState([]);
  const [novoMarcasPermitidas, setNovoMarcasPermitidas] = useState([...MARCAS_PERMITIDAS_DEFAULT]);

  useEffect(() => {
    loadOperadoras();
    loadTodosUsuarios();
  }, []);

  useEffect(() => {
    loadUsuarios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search]);

  const loadTodosUsuarios = async () => {
    try {
      const data = await usersService.getAll();
      setTodosUsuarios(Array.isArray(data) ? data : data.data || []);
    } catch (error) {
      console.error('Erro ao carregar lista completa de usuários:', error);
    }
  };

  const loadOperadoras = async () => {
    try {
      const data = await operadorasService.getAll();
      setOperadoras(data);
    } catch (error) {
      console.error('Erro ao carregar operadoras:', error);
    }
  };

  const loadUsuarios = async () => {
    try {
      setLoading(true);
      const data = await usersService.getAll({ page, limit: 20, search: search || undefined });
      if (data && data.pagination) {
        setUsuarios(data.data || []);
        setTotalPages(data.pagination.totalPages);
        setTotal(data.pagination.total);
      } else {
        setUsuarios(Array.isArray(data) ? data : []);
        setTotalPages(1);
        setTotal(Array.isArray(data) ? data.length : 0);
      }
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      alert('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const getRoleText = (role) => {
    const roles = {
      'admin': 'Administrador',
      'diretor': 'Diretor',
      'supervisor': 'Supervisor',
      'vendedor': 'Vendedor',
      'backoffice': 'BackOffice',
      'supervisor_bko': 'Supervisor BKO'
    };
    return roles[role] || role;
  };

  const getRoleClass = (role) => {
    const classes = {
      'admin': 'role-admin',
      'diretor': 'role-diretor',
      'supervisor': 'role-supervisor',
      'vendedor': 'role-vendedor',
      'backoffice': 'role-backoffice',
      'supervisor_bko': 'role-supervisor-bko'
    };
    return classes[role] || '';
  };

  const handleResetOtp = async (usuario) => {
    if (!window.confirm(`Resetar OTP do usuário ${usuario.nome}?\n\nNo próximo login ele precisará reconfigurar o Google Authenticator.`)) {
      return;
    }

    try {
      await usersService.resetOtp(usuario.id);
      alert(`✅ OTP resetado para ${usuario.nome}. Ele precisará reconfigurar no próximo login.`);
      loadUsuarios();
    } catch (error) {
      console.error('Erro ao resetar OTP:', error);
      alert('❌ Erro ao resetar OTP: ' + (error.response?.data?.error || 'Erro desconhecido'));
    }
  };

  const handleDelete = async (usuario) => {
    // Confirmação de exclusão
    const confirmMessage = `⚠️ ATENÇÃO!\n\nVocê está prestes a excluir:\n👤 Nome: ${usuario.nome}\n🔑 Usuário: ${usuario.username}\n\nEsta ação NÃO pode ser desfeita!\n\nDigite "EXCLUIR" para confirmar:`;
    
    const confirmation = prompt(confirmMessage);
    
    if (confirmation !== 'EXCLUIR') {
      alert('❌ Operação cancelada');
      return;
    }

    try {
      await usersService.delete(usuario.id);
      alert('✅ Usuário excluído com sucesso!');
      loadUsuarios();
    } catch (error) {
      console.error('Erro ao excluir usuário:', error);
      alert('❌ Erro ao excluir usuário: ' + (error.response?.data?.error || 'Erro desconhecido'));
    }
  };

  const handleEdit = async (usuario) => {
    setEditingUsuario({
      ...usuario,
      pap_marcas: resolvePapMarcas(usuario),
      marcas_permitidas: resolveMarcasPermitidas(usuario)
    });
    setEditingTelefones(usuario.telefones && usuario.telefones.length > 0 ? [...usuario.telefones] : (usuario.telefone ? [usuario.telefone] : []));
    
    // Se for backoffice, carregar operadoras do usuário
    if (usuario.role === 'backoffice') {
      try {
        const data = await usersService.getOperadoras(usuario.id);
        setUsuarioOperadoras(data.operadoras.map(op => op.id));
        setAtenderTodas(data.operadoras.length === 0);
      } catch (error) {
        console.error('Erro ao carregar operadoras do usuário:', error);
        setUsuarioOperadoras([]);
        setAtenderTodas(false);
      }
    } else {
      setUsuarioOperadoras([]);
      setAtenderTodas(false);
    }
  };

  // Funções para gerenciar múltiplos telefones
  const handleAddTelefone = () => {
    if (editingTelefones.length < 3) {
      setEditingTelefones([...editingTelefones, '']);
    }
  };

  const handleRemoveTelefone = (index) => {
    setEditingTelefones(editingTelefones.filter((_, i) => i !== index));
  };

  const handleTelefoneChange = (index, value) => {
    const novosTelefones = [...editingTelefones];
    novosTelefones[index] = value.replace(/\D/g, '').slice(0, 11);
    setEditingTelefones(novosTelefones);
  };

  // Funções para gerenciar múltiplos telefones no novo usuário
  const handleAddNovoTelefone = () => {
    if (novosTelefones.length < 3) {
      setNovosTelefones([...novosTelefones, '']);
    }
  };

  const handleRemoveNovoTelefone = (index) => {
    setNovosTelefones(novosTelefones.filter((_, i) => i !== index));
  };

  const handleNovoTelefoneChange = (index, value) => {
    const telefonesAtualizados = [...novosTelefones];
    telefonesAtualizados[index] = value.replace(/\D/g, '').slice(0, 11);
    setNovosTelefones(telefonesAtualizados);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      // Validar telefones
      const telefonesValidos = editingTelefones.filter(tel => tel && tel.trim() !== '');
      const todosTelefones = [...telefonesValidos];
      
      // Verificar duplicatas
      const telefonesUnicos = [...new Set(todosTelefones)];
      if (telefonesUnicos.length !== todosTelefones.length) {
        alert('❌ Não é permitido adicionar telefones duplicados');
        return;
      }
      
      // Validar formato dos telefones
      for (const tel of todosTelefones) {
        if (tel.length !== 10 && tel.length !== 11) {
          alert(`❌ Telefone inválido: ${tel}. Deve ter 10 ou 11 dígitos (DDD + número)`);
          return;
        }
      }

      const updateData = {
        nome: editingUsuario.nome,
        role: editingUsuario.role,
        supervisor_id: editingUsuario.supervisor_id || null,
        ativo: editingUsuario.ativo !== undefined ? editingUsuario.ativo : true,
        telefone: todosTelefones.length > 0 ? todosTelefones[0] : null,
        telefones: todosTelefones
      };
      if (editingUsuario.role === 'vendedor' || editingUsuario.role === 'supervisor') {
        updateData.pix = editingUsuario.pix != null ? String(editingUsuario.pix).trim() || null : null;
        const p = editingUsuario.percentual_vendedor != null && editingUsuario.percentual_vendedor !== '' ? parseFloat(editingUsuario.percentual_vendedor) : 100;
        updateData.percentual_vendedor = p;
        const pCpf = editingUsuario.percentual_vendedor_cpf != null && editingUsuario.percentual_vendedor_cpf !== '' ? parseFloat(editingUsuario.percentual_vendedor_cpf) : null;
        const pCnpj = editingUsuario.percentual_vendedor_cnpj != null && editingUsuario.percentual_vendedor_cnpj !== '' ? parseFloat(editingUsuario.percentual_vendedor_cnpj) : null;
        updateData.percentual_vendedor_cpf = pCpf;
        updateData.percentual_vendedor_cnpj = pCnpj;
        if (editingUsuario.role === 'supervisor') {
          const pEquipe = editingUsuario.percentual_equipe != null && editingUsuario.percentual_equipe !== '' ? parseFloat(editingUsuario.percentual_equipe) : null;
          updateData.percentual_equipe = pEquipe;
        }
        updateData.pap_marcas = editingUsuario.pap_marcas || [];
        updateData.marcas_permitidas = editingUsuario.marcas_permitidas || [];
        if (updateData.marcas_permitidas.length === 0) {
          alert('❌ Selecione ao menos uma marca permitida para vendas CPF');
          return;
        }
      }
      updateData.acesso_consulta_cadastral = editingUsuario.acesso_consulta_cadastral === true;
      updateData.acesso_como_funciona = editingUsuario.acesso_como_funciona === true;

      // Se tiver nova senha, adicionar
      if (editingUsuario.nova_senha && editingUsuario.nova_senha.trim() !== '') {
        if (editingUsuario.nova_senha.length < 6) {
          alert('❌ A senha deve ter no mínimo 6 caracteres');
          return;
        }
        updateData.password = editingUsuario.nova_senha;
      }

      await usersService.update(editingUsuario.id, updateData);
      
      // Se for backoffice, atualizar operadoras
      if (editingUsuario.role === 'backoffice') {
        try {
          await usersService.updateOperadoras(
            editingUsuario.id,
            atenderTodas ? [] : usuarioOperadoras,
            atenderTodas
          );
        } catch (error) {
          console.error('Erro ao atualizar operadoras:', error);
          alert('⚠️ Usuário atualizado, mas houve erro ao atualizar operadoras');
        }
      }
      
      alert('✅ Usuário atualizado com sucesso!');
      setEditingUsuario(null);
      setUsuarioOperadoras([]);
      setAtenderTodas(false);
      loadUsuarios();
      loadTodosUsuarios();
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
      alert('❌ Erro ao atualizar usuário: ' + (error.response?.data?.error || 'Erro desconhecido'));
    }
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData(e.target);
      const role = formData.get('role');
      
      // Coletar telefones do formulário
      const telefonePrincipal = formData.get('telefone') ? formData.get('telefone').replace(/\D/g, '') : null;
      const todosTelefones = [...novosTelefones.filter(tel => tel && tel.trim() !== '')];
      if (telefonePrincipal && !todosTelefones.includes(telefonePrincipal)) {
        todosTelefones.unshift(telefonePrincipal);
      }
      
      // Validar duplicatas
      const telefonesUnicos = [...new Set(todosTelefones)];
      if (telefonesUnicos.length !== todosTelefones.length) {
        alert('❌ Não é permitido adicionar telefones duplicados');
        return;
      }
      
      // Validar formato
      for (const tel of todosTelefones) {
        if (tel.length !== 10 && tel.length !== 11) {
          alert(`❌ Telefone inválido: ${tel}. Deve ter 10 ou 11 dígitos (DDD + número)`);
          return;
        }
      }
      
      // Validar limite
      if (todosTelefones.length > 3) {
        alert('❌ Máximo de 3 telefones permitidos');
        return;
      }
      
      const userData = {
        username: formData.get('username'),
        password: formData.get('password'),
        nome: formData.get('nome'),
        role,
        supervisor_id: formData.get('supervisor_id') || null,
        telefone: todosTelefones.length > 0 ? todosTelefones[0] : null,
        telefones: todosTelefones
      };
      if (role === 'vendedor' || role === 'supervisor') {
        const pix = formData.get('pix');
        userData.pix = pix ? String(pix).trim() || null : null;
        const perc = formData.get('percentual_vendedor');
        userData.percentual_vendedor = perc && !isNaN(parseFloat(perc)) ? parseFloat(perc) : 100;
        const percCpf = formData.get('percentual_vendedor_cpf');
        userData.percentual_vendedor_cpf = percCpf && !isNaN(parseFloat(percCpf)) ? parseFloat(percCpf) : null;
        const percCnpj = formData.get('percentual_vendedor_cnpj');
        userData.percentual_vendedor_cnpj = percCnpj && !isNaN(parseFloat(percCnpj)) ? parseFloat(percCnpj) : null;
        if (role === 'supervisor') {
          const percEquipe = formData.get('percentual_equipe');
          userData.percentual_equipe = percEquipe && !isNaN(parseFloat(percEquipe)) ? parseFloat(percEquipe) : null;
        }
        userData.pap_marcas = novoPapMarcas;
        userData.marcas_permitidas = novoMarcasPermitidas.length > 0 ? novoMarcasPermitidas : [...MARCAS_PERMITIDAS_DEFAULT];
      }
      userData.acesso_consulta_cadastral = formData.get('acesso_consulta_cadastral') === 'on';
      userData.acesso_como_funciona = formData.get('acesso_como_funciona') === 'on';

      if (userData.password.length < 6) {
        alert('❌ A senha deve ter no mínimo 6 caracteres');
        return;
      }

      await usersService.create(userData);
      alert('✅ Usuário criado com sucesso!');
      setShowNovoUsuario(false);
      setNovosTelefones([]);
      setNovoPapMarcas([]);
      setNovoMarcasPermitidas([...MARCAS_PERMITIDAS_DEFAULT]);
      loadUsuarios();
      loadTodosUsuarios();
      e.target.reset();
    } catch (error) {
      console.error('Erro ao criar usuário:', error);
      alert('❌ Erro ao criar usuário: ' + (error.response?.data?.error || 'Erro desconhecido'));
    }
  };

  const closeModal = () => {
    setEditingUsuario(null);
    setShowNovoUsuario(false);
    setUsuarioOperadoras([]);
    setAtenderTodas(false);
    setNovoUsuarioRole('');
    setEditingTelefones([]);
    setNovosTelefones([]);
    setNovoPapMarcas([]);
    setNovoMarcasPermitidas([...MARCAS_PERMITIDAS_DEFAULT]);
  };

  const renderMarcasCheckboxes = (selected, onChange) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.5rem' }}>
      {MARCAS.map((marca) => (
        <label key={marca.id} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={(selected || []).includes(marca.id)}
            onChange={(e) => onChange(toggleMarcaInArray(selected || [], marca.id, e.target.checked))}
            style={{ marginRight: '0.5rem' }}
          />
          {marca.label}
        </label>
      ))}
    </div>
  );

  const handleOperadoraToggle = (operadoraId) => {
    if (atenderTodas) {
      setAtenderTodas(false);
    }
    if (usuarioOperadoras.includes(operadoraId)) {
      setUsuarioOperadoras(usuarioOperadoras.filter(id => id !== operadoraId));
    } else {
      setUsuarioOperadoras([...usuarioOperadoras, operadoraId]);
    }
  };

  const handleAtenderTodasChange = (checked) => {
    setAtenderTodas(checked);
    if (checked) {
      setUsuarioOperadoras([]);
    }
  };

  return (
    <Layout>
      <div className="usuarios-page">
        {/* Header */}
        <div className="usuarios-header">
          <div>
            <h1 className="card-title">👥 Gerenciamento de Usuários</h1>
          </div>
          <div className="header-actions">
            <button onClick={() => navigate('/dashboard')} className="btn btn-secondary">
              🏠 Voltar ao Dashboard
            </button>
            <button onClick={() => setShowNovoUsuario(true)} className="btn btn-primary">
              ➕ Novo Usuário
            </button>
          </div>
        </div>

        <form className="usuarios-search" onSubmit={handleSearchSubmit} style={{ marginBottom: '1rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ flex: 1, minWidth: 220, margin: 0 }}>
            <label>Buscar por nome ou usuário</label>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Digite o nome..."
            />
          </div>
          <button type="submit" className="btn btn-primary">Buscar</button>
          {search && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setSearchInput('');
                setSearch('');
                setPage(1);
              }}
            >
              Limpar
            </button>
          )}
        </form>

        {/* Tabela de Usuários */}
        {loading ? (
          <div className="loading">Carregando usuários...</div>
        ) : usuarios.length === 0 ? (
          <div className="no-usuarios">
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📭</div>
            <h3>Nenhum usuário encontrado</h3>
            <p>Crie o primeiro usuário para começar</p>
            <button
              onClick={() => setShowNovoUsuario(true)}
              className="btn btn-primary"
              style={{ marginTop: '1rem' }}
            >
              ➕ Criar Primeiro Usuário
            </button>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="usuarios-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nome</th>
                  <th>Usuário</th>
                  <th>Hierarquia</th>
                  <th>Supervisor</th>
                  <th>Status</th>
                  <th>Consulta Cadastral</th>
                  <th>DID</th>
                  <th>OTP</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {usuarios.map(usuario => (
                  <tr key={usuario.id}>
                    <td>{usuario.id}</td>
                    <td>{usuario.nome}</td>
                    <td>{usuario.username}</td>
                    <td>
                      <span className={`role-badge ${getRoleClass(usuario.role)}`}>
                        {getRoleText(usuario.role)}
                      </span>
                    </td>
                    <td>
                      {usuario.supervisor ? usuario.supervisor.nome : '-'}
                    </td>
                    <td>
                      <span className={`status-badge ${usuario.ativo ? 'status-ativo' : 'status-inativo'}`}>
                        {usuario.ativo ? '✅ Ativo' : '❌ Inativo'}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${usuario.acesso_consulta_cadastral ? 'status-ativo' : 'status-inativo'}`} style={{ fontSize: '0.75rem' }}>
                        {usuario.acesso_consulta_cadastral ? '✅ Sim' : '❌ Não'}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${usuario.acesso_como_funciona ? 'status-ativo' : 'status-inativo'}`} style={{ fontSize: '0.75rem' }}>
                        {usuario.acesso_como_funciona ? '✅ Sim' : '❌ Não'}
                      </span>
                    </td>
                    <td>
                      {usuario.role === 'vendedor' ? (
                        <span className="status-badge" style={{ fontSize: '0.75rem', opacity: 0.6 }}>
                          N/A
                        </span>
                      ) : (
                        <span className={`status-badge ${usuario.otp_verified ? 'status-ativo' : 'status-inativo'}`} style={{ fontSize: '0.75rem' }}>
                          {usuario.otp_verified ? '✅ Ativo' : '⏳ Pendente'}
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => handleEdit(usuario)}
                          className="btn-small btn-edit"
                        >
                          Editar
                        </button>
                        {usuario.role !== 'vendedor' && (
                          <button
                            onClick={() => handleResetOtp(usuario)}
                            className="btn-small btn-edit"
                            disabled={!usuario.otp_verified}
                            title={!usuario.otp_verified ? 'Usuário ainda não configurou o OTP' : 'Reseta o OTP do usuário'}
                          >
                            🔑 Reset OTP
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(usuario)}
                          className="btn-small btn-danger"
                          disabled={usuario.username === 'admin' || usuario.id === user?.id}
                          title={usuario.username === 'admin' ? 'Não é possível excluir o usuário admin' : usuario.id === user?.id ? 'Você não pode excluir seu próprio usuário' : ''}
                        >
                          Excluir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '1rem', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Anterior
                </button>
                <span style={{ color: 'var(--texto-secundario)' }}>
                  Página {page} de {totalPages} ({total} usuários)
                </span>
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Próxima
                </button>
              </div>
            )}
          </div>
        )}

        {/* Modal de Editar Usuário */}
        {editingUsuario && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content-usuario" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header-usuario">
                <h2>✏️ Editar Usuário</h2>
                <span className="close-modal" onClick={closeModal}>×</span>
              </div>
              
              <form onSubmit={handleEditSubmit} className="modal-body-usuario">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Nome Completo *</label>
                    <input
                      type="text"
                      value={editingUsuario.nome || ''}
                      onChange={(e) => setEditingUsuario({ ...editingUsuario, nome: e.target.value })}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Usuário</label>
                    <input
                      type="text"
                      value={editingUsuario.username || ''}
                      disabled
                      style={{ background: 'var(--cinza-escuro-2)', opacity: 0.7 }}
                    />
                    <small>O usuário não pode ser alterado</small>
                  </div>

                  <div className="form-group">
                    <label>Hierarquia *</label>
                    <select
                      value={editingUsuario.role || ''}
                      onChange={(e) => setEditingUsuario({ ...editingUsuario, role: e.target.value })}
                      required
                    >
                      {user?.role === 'admin' && <option value="admin">Administrador</option>}
                      <option value="diretor">Diretor</option>
                      <option value="supervisor">Supervisor</option>
                      <option value="vendedor">Vendedor</option>
                      <option value="backoffice">BackOffice</option>
                      <option value="supervisor_bko">Supervisor BKO</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Status</label>
                    <select
                      value={editingUsuario.ativo ? 'true' : 'false'}
                      onChange={(e) => setEditingUsuario({ ...editingUsuario, ativo: e.target.value === 'true' })}
                    >
                      <option value="true">Ativo</option>
                      <option value="false">Inativo</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Supervisor</label>
                    <select
                      value={editingUsuario.supervisor_id || ''}
                      onChange={(e) => setEditingUsuario({ ...editingUsuario, supervisor_id: e.target.value ? parseInt(e.target.value) : null })}
                    >
                      <option value="">Sem supervisor</option>
                      {todosUsuarios
                        .filter(u => {
                          if (u.id === editingUsuario.id) return false;
                          
                          // BKO só pode ter supervisor_bko como supervisor
                          if (editingUsuario.role === 'backoffice') {
                            return u.role === 'supervisor_bko';
                          }
                          
                          // Vendedor só pode ter supervisor como supervisor
                          if (editingUsuario.role === 'vendedor') {
                            return u.role === 'supervisor';
                          }
                          
                          // Para outros roles, manter a lógica anterior
                          return u.role === 'supervisor' || u.role === 'supervisor_bko' || u.role === 'admin' || u.role === 'diretor';
                        })
                        .map(supervisor => (
                          <option key={supervisor.id} value={supervisor.id}>
                            {supervisor.nome} ({getRoleText(supervisor.role)})
                          </option>
                        ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Nova Senha (opcional)</label>
                    <input
                      type="password"
                      value={editingUsuario.nova_senha || ''}
                      onChange={(e) => setEditingUsuario({ ...editingUsuario, nova_senha: e.target.value })}
                      minLength="6"
                      placeholder="Deixe em branco para manter a senha atual"
                    />
                    <small>Mínimo 6 caracteres</small>
                  </div>

                  <div className="form-group full-width">
                    <label>Telefones para Notificações WhatsApp</label>
                    <div className="telefones-list">
                      {editingTelefones.map((tel, index) => (
                        <div key={index} className="telefone-item">
                          <input
                            type="text"
                            value={tel}
                            onChange={(e) => handleTelefoneChange(index, e.target.value)}
                            placeholder={`Telefone ${index + 1} (DDD + numero)`}
                            maxLength="11"
                          />
                          {editingTelefones.length > 1 && (
                            <button
                              type="button"
                              className="btn-remove-telefone"
                              onClick={() => handleRemoveTelefone(index)}
                              title="Remover telefone"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      ))}
                      {editingTelefones.length < 3 && (
                        <button
                          type="button"
                          className="btn-add-telefone"
                          onClick={handleAddTelefone}
                        >
                          + Adicionar Telefone
                        </button>
                      )}
                    </div>
                    <small>Maximo 3 telefones. Apenas numeros, DDD + numero (ex: 11999999999)</small>
                  </div>

                  {(editingUsuario.role === 'vendedor' || editingUsuario.role === 'supervisor') && (
                    <>
                      <div className="form-group">
                        <label>Percentual CPF (%)</label>
                        <input
                          type="number"
                          min="50"
                          max="300"
                          step="0.01"
                          value={editingUsuario.percentual_vendedor_cpf ?? ''}
                          onChange={(e) => setEditingUsuario({ ...editingUsuario, percentual_vendedor_cpf: e.target.value })}
                          placeholder="Ex: 100"
                        />
                        <small>Para vendas CPF. Se vazio, usa o percentual geral.</small>
                      </div>
                      <div className="form-group">
                        <label>Percentual CNPJ (%)</label>
                        <input
                          type="number"
                          min="50"
                          max="300"
                          step="0.01"
                          value={editingUsuario.percentual_vendedor_cnpj ?? ''}
                          onChange={(e) => setEditingUsuario({ ...editingUsuario, percentual_vendedor_cnpj: e.target.value })}
                          placeholder="Ex: 150"
                        />
                        <small>Para vendas CNPJ. Se vazio, usa o percentual geral.</small>
                      </div>
                      <div className="form-group">
                        <label>Percentual geral (%) — fallback</label>
                        <input
                          type="number"
                          min="50"
                          max="300"
                          step="0.01"
                          value={editingUsuario.percentual_vendedor ?? 100}
                          onChange={(e) => setEditingUsuario({ ...editingUsuario, percentual_vendedor: e.target.value })}
                          placeholder="100"
                        />
                        <small>Usado quando CPF/CNPJ não definidos. Ex: 100 = valor da venda, 150 = 150%.</small>
                      </div>
                      {editingUsuario.role === 'supervisor' && (
                        <div className="form-group">
                          <label>Percentual equipe (%)</label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            value={editingUsuario.percentual_equipe ?? ''}
                            onChange={(e) => setEditingUsuario({ ...editingUsuario, percentual_equipe: e.target.value })}
                            placeholder="Ex: 5"
                          />
                          <small>Quanto ganha sobre as vendas dos subordinados. Ex: 5% = recebe 5% do valor que cada vendedor recebe.</small>
                        </div>
                      )}
                      <div className="form-group">
                        <label>Chave PIX (opcional)</label>
                        <input
                          type="text"
                          value={editingUsuario.pix || ''}
                          onChange={(e) => setEditingUsuario({ ...editingUsuario, pix: e.target.value })}
                          placeholder="CPF, CNPJ, e-mail ou chave aleatória"
                        />
                        <small>Para pagamento. Aceita CPF, CNPJ, e-mail ou chave aleatória.</small>
                      </div>
                      <div className="form-group full-width">
                        <label><strong>Possui PAP em</strong></label>
                        {renderMarcasCheckboxes(
                          editingUsuario.pap_marcas,
                          (papMarcas) => setEditingUsuario({ ...editingUsuario, pap_marcas: papMarcas })
                        )}
                        <small>Marque as marcas em que o vendedor possui PAP. Em vendas CPF da marca correspondente será obrigatório Código PAP e Login PAP.</small>
                      </div>
                      <div className="form-group full-width">
                        <label><strong>Pode subir vendas em</strong></label>
                        {renderMarcasCheckboxes(
                          editingUsuario.marcas_permitidas,
                          (marcasPermitidas) => setEditingUsuario({ ...editingUsuario, marcas_permitidas: marcasPermitidas })
                        )}
                        <small>Marque onde o vendedor pode cadastrar vendas CPF. Selecione ao menos uma marca.</small>
                      </div>
                    </>
                  )}

                  <div className="form-group full-width">
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={editingUsuario.acesso_consulta_cadastral || false}
                        onChange={(e) => setEditingUsuario({ ...editingUsuario, acesso_consulta_cadastral: e.target.checked })}
                        style={{ marginRight: '0.5rem' }}
                      />
                      <strong>Acesso à Consulta Cadastral</strong>
                    </label>
                    <small>Permite consultar dados cadastrais por CPF. Desativado por padrão.</small>
                  </div>

                  <div className="form-group full-width">
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={editingUsuario.acesso_como_funciona || false}
                        onChange={(e) => setEditingUsuario({ ...editingUsuario, acesso_como_funciona: e.target.checked })}
                        style={{ marginRight: '0.5rem' }}
                      />
                      <strong>Acesso à página DID</strong>
                    </label>
                    <small>Permite comprar e gerenciar números virtuais (DID). Desativado por padrão.</small>
                  </div>

                  {editingUsuario.role === 'backoffice' && (
                    <div className="form-group full-width">
                      <label>Operadoras para Atender</label>
                      <div style={{ marginTop: '0.5rem' }}>
                        <label style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={atenderTodas}
                            onChange={(e) => handleAtenderTodasChange(e.target.checked)}
                            style={{ marginRight: '0.5rem' }}
                          />
                          <strong>Atender Todas as Operadoras</strong>
                        </label>
                        
                        {!atenderTodas && (
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '0.5rem' }}>
                            {operadoras.filter(op => op.ativo).map(operadora => (
                              <label
                                key={operadora.id}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  padding: '0.5rem',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px',
                                  cursor: 'pointer'
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={usuarioOperadoras.includes(operadora.id)}
                                  onChange={() => handleOperadoraToggle(operadora.id)}
                                  style={{ marginRight: '0.5rem' }}
                                />
                                {operadora.nome_display || operadora.nome.charAt(0).toUpperCase() + operadora.nome.slice(1)}
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                  <button type="button" onClick={closeModal} className="btn-secondary">
                    Cancelar
                  </button>
                  <button type="submit" className="btn-primary">
                    ✅ Salvar Alterações
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal de Novo Usuário */}
        {showNovoUsuario && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content-usuario" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header-usuario">
                <h2>➕ Novo Usuário</h2>
                <span className="close-modal" onClick={closeModal}>×</span>
              </div>
              
              <form onSubmit={handleCreateSubmit} className="modal-body-usuario">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Nome Completo *</label>
                    <input
                      type="text"
                      name="nome"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Usuário *</label>
                    <input
                      type="text"
                      name="username"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Hierarquia *</label>
                    <select 
                      name="role" 
                      required
                      value={novoUsuarioRole}
                      onChange={(e) => setNovoUsuarioRole(e.target.value)}
                    >
                      <option value="">Selecione a hierarquia</option>
                      <optgroup label="Administração">
                        {user?.role === 'admin' && <option value="admin">Administrador</option>}
                        <option value="diretor">Diretor</option>
                      </optgroup>
                      <optgroup label="Vendas">
                        <option value="supervisor">Supervisor</option>
                        <option value="vendedor">Vendedor</option>
                      </optgroup>
                      <optgroup label="BackOffice">
                        <option value="backoffice">BackOffice</option>
                        <option value="supervisor_bko">Supervisor BKO</option>
                      </optgroup>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Supervisor</label>
                    <select name="supervisor_id">
                      <option value="">Sem supervisor</option>
                      {todosUsuarios
                        .filter(u => {
                          // BKO só pode ter supervisor_bko como supervisor
                          if (novoUsuarioRole === 'backoffice') {
                            return u.role === 'supervisor_bko';
                          }
                          
                          // Vendedor só pode ter supervisor como supervisor
                          if (novoUsuarioRole === 'vendedor') {
                            return u.role === 'supervisor';
                          }
                          
                          // Para outros roles, manter a lógica anterior
                          return u.role === 'supervisor' || u.role === 'supervisor_bko' || u.role === 'admin' || u.role === 'diretor';
                        })
                        .map(supervisor => (
                          <option key={supervisor.id} value={supervisor.id}>
                            {supervisor.nome} ({getRoleText(supervisor.role)})
                          </option>
                        ))}
                    </select>
                  </div>

                  <div className="form-group full-width">
                    <label>Telefones para Notificacoes WhatsApp</label>
                    <div className="telefones-list">
                      <div className="telefone-item">
                        <input
                          type="text"
                          name="telefone"
                          placeholder="Telefone principal (DDD + numero)"
                          maxLength="11"
                        />
                      </div>
                      {novosTelefones.map((tel, index) => (
                        <div key={index} className="telefone-item">
                          <input
                            type="text"
                            value={tel}
                            onChange={(e) => handleNovoTelefoneChange(index, e.target.value)}
                            placeholder={`Telefone adicional ${index + 2}`}
                            maxLength="11"
                          />
                          <button
                            type="button"
                            className="btn-remove-telefone"
                            onClick={() => handleRemoveNovoTelefone(index)}
                            title="Remover telefone"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                      {(novosTelefones.length === 0 || novosTelefones.length < 2) && novosTelefones.length + 1 < 3 && (
                        <button
                          type="button"
                          className="btn-add-telefone"
                          onClick={handleAddNovoTelefone}
                        >
                          + Adicionar Telefone
                        </button>
                      )}
                    </div>
                    <small>Maximo 3 telefones. Apenas numeros, DDD + numero (ex: 11999999999)</small>
                  </div>

                  <div className="form-group">
                    <label>Senha *</label>
                    <input
                      type="password"
                      name="password"
                      required
                      minLength="6"
                    />
                    <small>Mínimo 6 caracteres</small>
                  </div>

                  {(novoUsuarioRole === 'vendedor' || novoUsuarioRole === 'supervisor') && (
                    <>
                      <div className="form-group">
                        <label>Percentual CPF (%)</label>
                        <input type="number" name="percentual_vendedor_cpf" min="50" max="300" step="0.01" placeholder="Ex: 100" />
                        <small>Para vendas CPF. Se vazio, usa o geral.</small>
                      </div>
                      <div className="form-group">
                        <label>Percentual CNPJ (%)</label>
                        <input type="number" name="percentual_vendedor_cnpj" min="50" max="300" step="0.01" placeholder="Ex: 150" />
                        <small>Para vendas CNPJ. Se vazio, usa o geral.</small>
                      </div>
                      <div className="form-group">
                        <label>Percentual geral (%)</label>
                        <input type="number" name="percentual_vendedor" min="50" max="300" step="0.01" defaultValue="100" placeholder="100" />
                        <small>Fallback quando CPF/CNPJ não definidos.</small>
                      </div>
                      {novoUsuarioRole === 'supervisor' && (
                        <div className="form-group">
                          <label>Percentual equipe (%)</label>
                          <input type="number" name="percentual_equipe" min="0" max="100" step="0.01" placeholder="Ex: 5" />
                          <small>Quanto ganha sobre as vendas dos subordinados.</small>
                        </div>
                      )}
                      <div className="form-group">
                        <label>Chave PIX (opcional)</label>
                        <input
                          type="text"
                          name="pix"
                          placeholder="CPF, CNPJ, e-mail ou chave aleatória"
                        />
                        <small>Para pagamento. Aceita CPF, CNPJ, e-mail ou chave aleatória.</small>
                      </div>
                      <div className="form-group full-width">
                        <label><strong>Possui PAP em</strong></label>
                        {renderMarcasCheckboxes(novoPapMarcas, setNovoPapMarcas)}
                        <small>Marque as marcas em que o vendedor possui PAP. Em vendas CPF da marca correspondente será obrigatório Código PAP e Login PAP.</small>
                      </div>
                      <div className="form-group full-width">
                        <label><strong>Pode subir vendas em</strong></label>
                        {renderMarcasCheckboxes(novoMarcasPermitidas, setNovoMarcasPermitidas)}
                        <small>Marque onde o vendedor pode cadastrar vendas CPF. Selecione ao menos uma marca.</small>
                      </div>
                    </>
                  )}

                  <div className="form-group full-width">
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        name="acesso_consulta_cadastral"
                        style={{ marginRight: '0.5rem' }}
                      />
                      <strong>Acesso à Consulta Cadastral</strong>
                    </label>
                    <small>Permite consultar dados cadastrais por CPF. Desativado por padrão.</small>
                  </div>

                  <div className="form-group full-width">
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        name="acesso_como_funciona"
                        style={{ marginRight: '0.5rem' }}
                      />
                      <strong>Acesso à página DID</strong>
                    </label>
                    <small>Permite comprar e gerenciar números virtuais (DID). Desativado por padrão.</small>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                  <button type="button" onClick={closeModal} className="btn-secondary">
                    Cancelar
                  </button>
                  <button type="submit" className="btn-primary">
                    ✅ Criar Usuário
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Usuarios;
